/* Dawn's sticky-header custom element. Moved out of sections/header.liquid (2026-09-11):
   4.5 KB of inline JS on every page. The element upgrades when this deferred asset defines it;
   nothing above the fold depends on it before DOMContentLoaded. */
  class StickyHeader extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.header = document.querySelector('.section-header');
      this.headerIsAlwaysSticky = this.getAttribute('data-sticky-type') === 'always' || this.getAttribute('data-sticky-type') === 'reduce-logo-size';
      this.headerBounds = {};

      this.setHeaderHeight();

      window.matchMedia('(max-width: 990px)').addEventListener('change', this.setHeaderHeight.bind(this));

      if (this.headerIsAlwaysSticky) {
        this.header.classList.add('shopify-section-header-sticky');
      };

      this.currentScrollTop = 0;
      this.preventReveal = false;
      this.predictiveSearch = this.querySelector('predictive-search');

      /* Scroll cost (2026-10-04, 390px / 4x CPU, 10 s of scrolling on the Jade page): reading scrollY inside the scroll
         event forced a full style recalc + layout every frame (the frame's own recalc then ran a second time), 180-260 ms
         per 10 s, the largest script cost left on the page. The scroll event now only queues one read; the read runs in
         the task right after the frame has painted, when layout is already clean, so it costs nothing. The decision and
         the classes are exactly as before; they land on the next frame instead of the current one (16 ms). */
      this.onScrollHandler = this.queueScroll.bind(this);
      this.onLateScroll = () => { this.scrollQueued = false; this.onScroll(); };
      this.hideHeaderOnScrollUp = () => this.preventReveal = true;

      this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.addEventListener('scroll', this.onScrollHandler, { passive: true });

      this.createObserver();
    }

    setHeaderHeight() {
      document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
    }

    disconnectedCallback() {
      this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.removeEventListener('scroll', this.onScrollHandler, { passive: true });
    }

    createObserver() {
      let observer = new IntersectionObserver((entries, observer) => {
        this.headerBounds = entries[0].intersectionRect;
        observer.disconnect();
      });

      observer.observe(this.header);
    }

    /* one read per frame at most, in a task after the frame (see connectedCallback) */
    queueScroll() {
      if (this.scrollQueued) return;
      this.scrollQueued = true;
      setTimeout(this.onLateScroll, 0);
    }

    onScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

      if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.setClasses(['scrolled-past-header'], []);
        if (this.preventHide) return;
        this.hide();
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.setClasses(['scrolled-past-header'], []);
        if (!this.preventReveal) {
          this.reveal();
        } else {
          window.clearTimeout(this.isScrolling);

          this.isScrolling = setTimeout(() => {
            this.preventReveal = false;
          }, 66);

          this.hide();
        }
      } else if (scrollTop <= this.headerBounds.top) {
        this.setClasses([], ['scrolled-past-header']);
        this.reset();
      }

      this.currentScrollTop = scrollTop;
    }

    /* class writes only when they change something: the same add/remove on every scroll event was an attribute
       mutation per frame (session-replay and other MutationObservers record each one) */
    setClasses(add, remove) {
      const list = this.header.classList;
      add.forEach((c) => { if (!list.contains(c)) list.add(c); });
      remove.forEach((c) => { if (list.contains(c)) list.remove(c); });
    }

    hide() {
      if (this.headerIsAlwaysSticky) return;
      this.setClasses(['shopify-section-header-hidden', 'shopify-section-header-sticky'], []);
      this.closeMenuDisclosure();
      this.closeSearchModal();
    }

    reveal() {
      if (this.headerIsAlwaysSticky) return;
      this.setClasses(['shopify-section-header-sticky', 'animate'], ['shopify-section-header-hidden']);
    }

    reset() {
      if (this.headerIsAlwaysSticky) return;
      this.setClasses([], ['shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate']);
    }

    closeMenuDisclosure() {
      this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
      /* The same race as closeSearchModal below, and the same guard. This one
         throws nothing today only because menu_type_desktop is 'mega', so no
         <header-menu> is rendered at all and the loop has nothing to walk.
         Switching the header to the dropdown menu in the editor would turn a
         scroll during page load into a thrown TypeError. */
      this.disclosures.forEach(disclosure => {
        if (typeof disclosure.close === 'function') disclosure.close();
      });
    }

    closeSearchModal() {
      this.searchModal = this.searchModal || this.header.querySelector('details-modal');
      /* details-modal.js is deferred; a scroll that fires before the element
         upgrades met a plain <details-modal> with no close(). PostHog logged
         "this.searchModal.close is not a function" 7x on / in 30 days. */
      /* Only an OPEN modal is closed. close() on a closed one only re-ran its cleanup: it took 'overflow-hidden' off
         <body> and dropped the document focus trap, which belong to whatever else is open (a drawer), on every
         scroll-down frame. */
      if (this.searchModal && typeof this.searchModal.close === 'function' &&
          !(typeof this.searchModal.isOpen === 'function' && !this.searchModal.isOpen())) this.searchModal.close(false);
    }
  }

  customElements.define('sticky-header', StickyHeader);
